/**
 * Public citizen advisor demo — same golden Studio App as IF-sf-application-advisor-v1,
 * skinned by jurisdiction package (city). One App/Flow template × N packages.
 */

import goldenAdvisorApp from '../../../packages/builder-schema/fixtures/golden-exports/IF-sf-application-advisor-v1.app.json';
import { applyJurisdictionToAdvisorApp } from '../../../packages/builder-schema/src/applyJurisdictionToAdvisorApp.mjs';
import { adviseApplicationType } from '../utils/applicationTypeAdvisor';
import {
  fetchJurisdictionPackage,
  getFallbackJurisdictionPackage,
} from './sfAdvisorIntake';
import sfDiscoveryExtras from './jurisdictions/san-francisco/v1/discovery-extras.json';

export const PERMIT_PUBLIC_DEMO_ID = 'if-sf-application-advisor-demo';
export const ADVISOR_GOLDEN_APP_NAME = 'IF-sf-application-advisor-v1';

function illustrativeFeesFromRouting(r, extras = sfDiscoveryExtras) {
  const schedule = extras?.feeSchedule || {};
  const ids = (r?.applicationTypes || []).map((t) => t.id).filter(Boolean);
  const lineItems = [];
  let total = 0;
  ids.forEach((id) => {
    const fee = schedule[id];
    if (!fee) return;
    const base = Number(fee.base) || 0;
    total += base;
    lineItems.push({
      applicationTypeId: id,
      label: fee.label || id,
      base,
      perSqft: Number(fee.perSqft) || 0,
      sqftFee: 0,
      total: base,
      currency: fee.currency || 'USD',
    });
  });
  return {
    success: true,
    currency: 'USD',
    approxSqft: 0,
    lineItems,
    total: Math.round(total * 100) / 100,
    note: 'Illustrative estimates for demo only — not official City fees.',
    disclaimer: 'Illustrative estimates for demo only — not official City fees.',
  };
}

function discoveryExtrasFromPkg(pkg) {
  return pkg?.discoveryExtras || pkg?.intake?.discoveryExtras || sfDiscoveryExtras;
}

function cityPreviewDefaults(pkg, jurisdictionId) {
  const id = pkg?.packageId || jurisdictionId || 'san-francisco';
  if (id === 'berkeley' || id === 'demo-generic') {
    return {
      description: 'Build an ADU behind my house',
      projectType: 'New construction',
      chatUser: 'I want to build an ADU behind my house. What do I apply for?',
    };
  }
  if (id === 'san-jose') {
    return {
      description: 'Minor kitchen remodel in my house',
      projectType: 'Renovation / Remodel',
      chatUser: 'I want a minor kitchen remodel in San José. What permits do I need?',
    };
  }
  return {
    description: 'Build an ADU behind my house in the Mission',
    projectType: 'New construction',
    chatUser: 'I want to build an ADU behind my house in the Mission. What do I apply for?',
  };
}

function buildPreviewAdvice(defaults = {}, pkg = null) {
  const jurisdictionId = pkg?.packageId || pkg?.catalog?.id || 'san-francisco';
  const cityHint = pkg?.name || pkg?.catalog?.name || 'San Francisco, CA';
  const preview = cityPreviewDefaults(pkg, jurisdictionId);
  const intent = {
    description: defaults.description || preview.description,
    projectType: defaults.projectType || defaults.propertyType || preview.projectType,
    propertyType: defaults.propertyType || defaults.projectType,
    category: defaults.category,
    proposedUse: defaults.proposedUse,
    location: defaults.address || defaults.location || cityHint,
    address: defaults.address,
  };
  const catalog = pkg?.catalog || jurisdictionId;
  const sampleAdvice = adviseApplicationType(intent, catalog);
  const routing = sampleAdvice.result.routing;
  const checklist = sampleAdvice.result.checklist;
  const summaryContent = sampleAdvice.result.summary.content;
  const previewFees = illustrativeFeesFromRouting(routing, discoveryExtrasFromPkg(pkg));
  if (previewFees.lineItems.length) routing.fees = previewFees;
  return {
    routing, checklist, summaryContent, previewFees, chatUser: preview.chatUser,
  };
}

/**
 * Build citizen demo definition from golden IF-sf-application-advisor-v1 + package skin.
 * @param {{ portalId?: string, defaults?: object, theme?: object, pkg?: object, jurisdictionId?: string, citizenMode?: boolean }=} opts
 */
export function buildPermitPublicDemoDefinition(opts = {}) {
  const jurisdictionId = opts.jurisdictionId || opts.pkg?.packageId || 'san-francisco';
  const pkg = opts.pkg || getFallbackJurisdictionPackage(jurisdictionId);
  const portalId = opts.portalId || null;
  const portals = pkg?.intake?.portals || {};
  const variant = portalId ? portals[portalId] : null;
  const defaults = opts.defaults || variant?.defaults || {};

  const app = applyJurisdictionToAdvisorApp(goldenAdvisorApp, pkg, {
    packageId: pkg.packageId || jurisdictionId,
    portalId,
    defaults,
    theme: opts.theme || variant?.theme,
    citizenMode: opts.citizenMode !== false,
    // Public demos use catalog advise + CLAW preview; Studio seed omits publicAdvise for live skill loop.
    publicAdvise: opts.publicAdvise !== false,
  });

  // Stable public demo ids (not Mongo ObjectIds)
  app.id = portalId ? `${PERMIT_PUBLIC_DEMO_ID}-${portalId}` : PERMIT_PUBLIC_DEMO_ID;
  app.definitionId = ADVISOR_GOLDEN_APP_NAME;

  const {
    routing, checklist, summaryContent, previewFees, chatUser,
  } = buildPreviewAdvice(defaults, pkg);

  const iflow = app.metadata.interviewFlow;
  iflow.previewChatHistory = [
    {
      role: 'user',
      content: defaults.description || chatUser,
    },
    {
      role: 'assistant',
      content: [
        `**Primary path: ${routing.primaryTrackLabel}**`,
        '',
        routing.rationale,
        '',
        'Applications to consider:',
        ...routing.applicationTypes.map((t) => `- ${t.label} (${t.track})`),
        '',
        '_Guidance from public sources — not an official determination._',
      ].join('\n'),
    },
  ];
  iflow.previewPanelOutputs = {
    left_checklist: {
      table_data: { columns: checklist.columns, rows: checklist.rows },
    },
    left_summary: { rich_text: { content: summaryContent } },
  };
  iflow.previewStructuredOutput = {
    routing,
    fees: previewFees.lineItems.length ? previewFees : undefined,
    table_data: checklist,
    checklist,
    rich_text: { content: summaryContent },
    summary: { content: summaryContent },
  };

  return app;
}

export const permitPublicDemoDefinition = buildPermitPublicDemoDefinition();

export async function loadPermitPublicDemoDefinition(opts = {}) {
  const jurisdictionId = opts.jurisdictionId || opts.pkg?.packageId || 'san-francisco';
  let pkg = opts.pkg;
  if (!pkg) {
    try {
      pkg = await fetchJurisdictionPackage(jurisdictionId);
    } catch (_) {
      pkg = getFallbackJurisdictionPackage(jurisdictionId);
    }
  }
  return buildPermitPublicDemoDefinition({
    ...opts,
    pkg,
    jurisdictionId: pkg.packageId || jurisdictionId,
  });
}
